'use client';

import { useId } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { BotMessageHeading } from '../../_parts/BotMessageHeading';
import { IntakeFields, type IntakeFormValues } from './IntakeFields';
import type { IntakeFieldErrors, IntakeFieldName } from '../intake-validation';
import { INTAKE_INTRO_ID, INTAKE_INTRO_TEMPLATE } from '../intake-copy';
import { useWidgetI18n } from '../../../i18n/WidgetI18nProvider';
import { visuallyHiddenSx } from '../../visuallyHiddenSx';

type Props = {
  agentName: string;
  institutionName: string;
  values: IntakeFormValues;
  detectedIso: string | null;
  errors: IntakeFieldErrors;
  emailSuggestion: string | null;
  submitError: string | null;
  submitting: boolean;
  formRef: React.RefObject<HTMLFormElement | null>;
  bannerRef: React.RefObject<HTMLDivElement | null>;
  onChange: (field: keyof IntakeFormValues, value: string | boolean) => void;
  onBlurField: (field: IntakeFieldName) => void;
  onApplyEmailSuggestion: () => void;
  onClearSubmitError: () => void;
  onSubmit: (e: React.FormEvent) => void;
};

export function IntakeFormShell({
  agentName,
  institutionName,
  values,
  detectedIso,
  errors,
  emailSuggestion,
  submitError,
  submitting,
  formRef,
  bannerRef,
  onChange,
  onBlurField,
  onApplyEmailSuggestion,
  onClearSubmitError,
  onSubmit,
}: Props) {
  const theme = useTheme();
  const { t, locale } = useWidgetI18n();
  const formLabelId = useId();
  const bannerTextId = useId();
  const intro = INTAKE_INTRO_TEMPLATE.replace(
    '{institution}',
    institutionName || 'your school'
  );

  return (
    <Paper
      ref={formRef}
      component="form"
      noValidate
      onSubmit={onSubmit}
      elevation={0}
      aria-labelledby={formLabelId}
      sx={{
        mx: 2,
        my: 1.5,
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        // Keep focused fields, the banner and Continue clear of the locked composer bar.
        '& input, & button, & [tabindex="-1"]': { scrollMarginBottom: '96px' },
      }}
    >
      <Typography
        component="span"
        id={formLabelId}
        lang={locale}
        sx={visuallyHiddenSx}
      >
        {t('intake.form')}
      </Typography>
      <BotMessageHeading agentName={agentName} />
      <Typography
        id={INTAKE_INTRO_ID}
        // Programmatic focus target for the Skip link and focus-on-open.
        tabIndex={-1}
        variant="body2"
        color="text.secondary"
        sx={{ mb: 1, mt: 0, lineHeight: 1.45, outline: 'none' }}
      >
        {intro}
      </Typography>
      <IntakeFields
        values={values}
        detectedIso={detectedIso}
        errors={errors}
        emailSuggestion={emailSuggestion}
        institutionName={institutionName}
        onChange={onChange}
        onBlurField={onBlurField}
        onApplyEmailSuggestion={onApplyEmailSuggestion}
      />
      {submitError && (
        <Alert
          ref={bannerRef}
          tabIndex={-1}
          // Not a live region: focus moves here, so a role="alert" would read it twice.
          role="group"
          aria-labelledby={bannerTextId}
          severity="error"
          sx={{ mt: 1.5, scrollMarginBottom: '96px' }}
          onClose={onClearSubmitError}
        >
          <span id={bannerTextId}>{submitError}</span>
        </Alert>
      )}
      <Box sx={{ mt: 2.5 }}>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disableElevation
          // aria-disabled, not disabled: a disabled button drops focus to the body mid-submit.
          // The submit handler ignores presses while submitting.
          aria-disabled={submitting}
          disableRipple={submitting}
          sx={{
            py: 1.25,
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 1.5,
            '&[aria-disabled="true"]': { cursor: 'default' },
          }}
        >
          {submitting ? (
            <>
              <CircularProgress size={22} color="inherit" aria-hidden />
              <Box component="span" sx={visuallyHiddenSx}>
                Continue
              </Box>
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </Box>
    </Paper>
  );
}
